import React, { useState, useEffect } from 'react'

import PropTypes from 'prop-types'

import {
  FormGrid,
  FormControl,
  Input,
  DatePicker,
  ButtonToggle,
  ButtonSelect,
} from 'components/form'

import { DestinationControl } from './destinationControl'
import { Button } from '../button'

import { useForm, Controller } from 'react-hook-form'

import styles from './inquiryForm.module.scss'
import { PhoneInput } from 'app/components/form/phoneInput'

import { isPossiblePhoneNumber } from 'react-phone-number-input'
import { registerIncompleteRequest, registerRequest } from 'app/tas'
import { usePageProperties } from 'hooks/usePageProperies'
import { useGTM } from 'app/GTM'
import { useCabinClass } from 'hooks/useCabinClass'

const isValidEmail = (email) => {
  const reg = /^\w+([_.-]\w+)*\w@\w((\.\w)*\w+)*(-)*((\.\w)*\w+)*\.\w{2,3}$/i

  return email && reg.test(email?.toLowerCase())
}

const isValidPhone = (phone) => {
  return phone && isPossiblePhoneNumber(phone)
}

const validateForm = (value) => {
  const errors = {}

  if (!value.from?.value) {
    errors.from = 'From not provided'
  }

  if (!value.to?.value) {
    errors.to = 'To not provided'
  }

  if (value.from && value.from.cityName === value.to?.cityName) {
    errors.to = 'Destination city cannot be the same as departure'
  }

  // validate phone
  if (!isValidPhone(value.phone)) {
    errors.phone = 'Invalid phone number'
  }

  if (!isValidEmail(value.email)) {
    errors.email = 'Email not provided'
  }

  return errors
}

const InquiryForm = ({ onSubmitOk, className = '', defaultClass = 'economy', ...rest }) => {
  const { register, handleSubmit, reset, control } = useForm({})

  const [resetTrigger, setResteTrigger] = useState(0)
  const [tripType, setTripType] = useState()

  const cabinClassCtx = useCabinClass()
  const pageProps = usePageProperties()

  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const tripOptions = {
    name: 'tripType',
    idBy: 'type',
    displayBy: 'display',
    options: [
      { type: 'RT', display: 'Round-trip' },
      { type: 'OW', display: 'One-way' },
    ],
    defaultValue: 'RT',
  }

  const classOptions = {
    name: 'class',
    label: `${defaultClass.charAt(0).toUpperCase()}${defaultClass.slice(1)}`,
    overlayClass: styles.inquiryForm__buttonSelect_overlay,
    idBy: 'id',
    displayBy: 'display',
    options: [
      { id: 'economy', display: 'Economy' },
      { id: 'premium', display: 'Premium' },
      { id: 'business', display: 'Business' },
      { id: 'first', display: 'First' },
    ],
  }

  const resetForm = () => {
    setIsLoading(false)
    setResteTrigger(resetTrigger + 1)
    setTripType(tripOptions.defaultValue)
    reset({
      class: defaultClass,
    })
  }

  const onSubmit = (data) => {
    data.email = data?.email?.trim()

    const err = validateForm(data)
    setErrors(err)

    // pluck values from destinations
    const finalFormValue = {
      ...data,
      from: data.from?.value,
      to: data.to?.value,
    }

    const props = { ...pageProps, ...finalFormValue }

    // email, phone, from, to should be valid to send complete reques
    // otherwise incomplete request is sent
    if (Object.keys(err).length) {
      registerIncompleteRequest(props)
    } else {
      setIsLoading(true)

      registerRequest(props, (discountId) => {
        resetForm()
        useGTM().sendToGtm({
          event: 'new_lead',
          formLocation: 'Request quote',
        })

        if (typeof onSubmitOk === 'function') {
          onSubmitOk(discountId)
        }
      })
    }
  }

  const fieldChange = (field) => {
    delete errors[field]
    setErrors({ ...errors })
  }

  // reset form on init - ff bug - email is filled on reload
  useEffect(() => resetForm(), [])

  const loadingIndicator = (
    <div className={styles.inquiryForm__spinner_container}>
      <svg className={styles.inquiryForm__spinner} viewBox="0 0 50 50">
        <circle
          className={styles.inquiryForm__spinnerPath}
          cx="25"
          cy="25"
          r="20"
          fill="none"
          strokeWidth="5"
        ></circle>
      </svg>
    </div>
  )

  const submitButton = (
    <Button className={`${styles.inquiryForm__submit}`} type="submit" disabled={isLoading}>
      <span style={{ color: isLoading ? 'transparent' : 'inherit' }}>Request a FREE Quote</span>
      {isLoading && loadingIndicator}
    </Button>
  )

  return (
    <div className={`${styles.inquiryForm} ${className}`} {...rest}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.inquiryForm__tripType}>
          <Controller
            control={control}
            name={tripOptions.name}
            defaultValue={tripOptions.defaultValue}
            render={({ value, onChange }) => (
              <ButtonToggle
                {...tripOptions}
                value={value}
                onChange={(v) => {
                  setTripType(v), onChange(v)
                }}
              />
            )}
          />

          <Controller
            control={control}
            name={classOptions.name}
            defaultValue={defaultClass}
            render={({ value, onChange }) => (
              <ButtonSelect
                {...classOptions}
                value={value}
                onChange={(v) => {
                  cabinClassCtx?.setCabinClass(v)
                  onChange(v)
                }}
              />
            )}
          />
        </div>

        <FormGrid>
          <FormControl>
            <Controller
              control={control}
              name="from"
              defaultValue=""
              render={({ value, onChange }) => (
                <DestinationControl
                  label="From"
                  id="inquiry_from"
                  required
                  error={errors?.from}
                  value={value}
                  onChange={(v) => {
                    fieldChange('from'), onChange(v)
                  }}
                  resetTrigger={resetTrigger}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck="false"
                />
              )}
            />
          </FormControl>
          <FormControl>
            <Controller
              control={control}
              name="to"
              defaultValue=""
              render={({ value, onChange }) => (
                <DestinationControl
                  name="to"
                  label="To"
                  id="inquiry_to"
                  required
                  error={errors?.to}
                  value={value}
                  onChange={(v) => {
                    fieldChange('to'), onChange(v)
                  }}
                  resetTrigger={resetTrigger}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck="false"
                />
              )}
            />
          </FormControl>
          <DatePicker
            idFrom="inquiry_departure_date"
            idTo="inquiry_arrival_date"
            nameFrom="departureDate"
            nameTo="arrivalDate"
            refFrom={register}
            refTo={register}
            errorFrom={errors?.departureDate}
            errorTo={errors?.arrivalDate}
            type={tripType || tripOptions.defaultValue}
            resetTrigger={resetTrigger}
          />
          <FormControl>
            <Controller
              control={control}
              name="phone"
              defaultValue=""
              render={({ value, onChange }) => (
                <PhoneInput
                  label="Phone"
                  id="inquiry_phone"
                  required
                  error={errors?.phone}
                  value={value}
                  onChange={(v) => {
                    fieldChange('phone'), onChange(v)
                  }}
                />
              )}
            />
          </FormControl>
          <FormControl>
            <Input
              name="email"
              label="Email"
              id="inquiry_email"
              error={errors?.email}
              onChange={() => fieldChange('email')}
              ref={register}
              autoComplete="email"
              autoCorrect="off"
              required
            />
          </FormControl>
        </FormGrid>

        {submitButton}
      </form>
    </div>
  )
}

InquiryForm.propTypes = {
  className: PropTypes.string,
  onSubmitOk: PropTypes.func,
  defaultClass: PropTypes.string,
}

export { InquiryForm }
